import { useLiveQuery } from 'dexie-react-hooks';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { birrInputValue, monthOf, newId, PAYMENT_METHODS, tryParseBirr, type PaymentMethod, type RecurringExpenseRecord } from '@dairy/shared';
import { useSession } from '@/auth/useSession';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Checkbox, Dialog, EmptyState, Field, Input, PageHeader, Select } from '@/components/ui';
import { IntInput, MoneyInput } from '@/components/inputs';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { save } from '@/db/repo';
import { useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { CategorySelect, categoryName, useCategories } from './CategorySelect';

/** Screen 19 — Recurring expenses (EX-4, owner): the server creates each month's expense on its day. */
export function RecurringExpensesPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const locale = useSession().profile?.locale ?? 'am';
  const items = useLiveQuery(() => db.recurringExpenses.toArray(), []) ?? [];
  const cats = useCategories();
  const [editing, setEditing] = useState<RecurringExpenseRecord | 'new' | null>(null);
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('recurring.title')}
        actions={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> {t('common.add')}
          </Button>
        }
      />
      <p className="text-sm text-stone-600">{t('recurring.help')}</p>
      {items.length === 0 ? <EmptyState>{t('recurring.empty')}</EmptyState> : null}
      <ul className="space-y-2">
        {items.map((r) => (
          <li key={r.id}>
            <Card className="flex items-center gap-3 p-3">
              <button className="min-w-0 flex-1 text-left" onClick={() => setEditing(r)}>
                <div className="font-semibold">
                  {r.description} {!r.active ? <Badge>{t('common.inactive')}</Badge> : null}
                </div>
                <div className="text-sm text-stone-600">
                  {categoryName(cats.find((c) => c.id === r.categoryId), locale)} · {t('recurring.dayOfMonth', { day: r.dayOfMonth })}
                </div>
              </button>
              <span className="font-bold tabular-nums">{f.birr(r.amountSantim)}</span>
            </Card>
          </li>
        ))}
      </ul>
      {editing ? <RecurringDialog item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function RecurringDialog({ item, onClose }: { item: RecurringExpenseRecord | null; onClose: () => void }) {
  const { t } = useTranslation();
  const today = useToday();
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [amount, setAmount] = useState(birrInputValue(item?.amountSantim));
  const [day, setDay] = useState(String(item?.dayOfMonth ?? 1));
  const [method, setMethod] = useState<PaymentMethod>(item?.method ?? 'CASH');
  const [paidTo, setPaidTo] = useState(item?.paidTo ?? '');
  const [start, setStart] = useState((item?.startMonth ?? `${monthOf(today)}-01`).slice(0, 7));
  const [end, setEnd] = useState(item?.endMonth?.slice(0, 7) ?? '');
  const [active, setActive] = useState(item?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const a = tryParseBirr(amount);
    const d = Number(day);
    if (!categoryId || !description.trim()) return setError(t('recurring.errors.required'));
    if (!a.ok || !a.santim) return setError(t('expenses.errors.amount'));
    if (!Number.isInteger(d) || d < 1 || d > 28) return setError(t('recurring.errors.day'));
    if (end && end < start) return setError(t('recurring.errors.end'));
    try {
      await save('recurringExpense', {
        ...(item ?? {}),
        id: item?.id ?? newId(),
        categoryId,
        description: description.trim(),
        amountSantim: a.santim,
        dayOfMonth: d,
        method,
        paidTo: paidTo.trim() || null,
        startMonth: `${start}-01`,
        endMonth: end ? `${end}-01` : null,
        active,
      });
      toast(t('common.saved'));
      onClose();
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={item ? t('recurring.edit') : t('recurring.add')} footer={<Button onClick={() => void submit()}>{t('common.save')}</Button>}>
      <div className="space-y-3">
        <Field label={t('expenses.category')}>
          <CategorySelect value={categoryId} onChange={setCategoryId} includeInactiveId={item?.categoryId} />
        </Field>
        <Field label={t('money.description')}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} placeholder={t('recurring.example')} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('money.amount')}>
            <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label={t('recurring.day')}>
            <IntInput value={day} onChange={(e) => setDay(e.target.value.replace(/\D/g, '').slice(0, 2))} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('recurring.startMonth')}>
            <Input type="month" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label={t('recurring.endMonth')}>
            <Input type="month" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <Field label={t('money.method')}>
          <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`money.methodType.${m}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('expenses.paidTo')}>
          <Input value={paidTo} onChange={(e) => setPaidTo(e.target.value)} maxLength={80} />
        </Field>
        <Checkbox label={t('common.active')} checked={active} onChange={(e) => setActive(e.target.checked)} />
        {error ? <p className="text-red-700">{error}</p> : null}
      </div>
    </Dialog>
  );
}
